// @ts-nocheck
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./CustomDateRange.scss";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

import { DateRangePicker } from "react-date-range";

import moment from "moment";
import classNames from "classnames";
import { addDays } from "date-fns";
import { usePopper } from "react-popper";
import { Portal } from "react-overlays";
import { overlaysRef } from "src/App";
import zIndex from "src/utils/zIndex";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import useOnClickOutside from "../../methods/useOnClickOutside";
import { ReactComponent as CrossIcon } from "../../../images/cross.svg";
import { ReactComponent as CalendarIcon } from "../../../images/calendar.svg";
import { momentDateFormat } from "../CustomDatePicker/utils";

interface Props {
  dateFrom: string | null;
  setDateFrom: (value: string | null) => void;
  dateTo: string | null;
  setDateTo: (value: string | null) => void;
  startingRangeDays?: number;
  showError?: boolean;
  errorMessage?: string | ReactNode;
  toRight?: boolean;
  minDate?: Date;
  maxDate?: Date;
  setIsPickerOpen?: Dispatch<SetStateAction<boolean>>;
  dataQa?: string;
  onOpen?: () => void;
  hideClearButton?: boolean;
  onRangeComplete?: (dateFrom: string, dateTo: string) => void;
}

export const CustomDateRange: React.FC<Props> = (props) => {
  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    startingRangeDays,
    showError,
    errorMessage,
    minDate,
    maxDate,
    toRight,
    dataQa,
    setIsPickerOpen,
    onOpen,
    hideClearButton,
    onRangeComplete,
  } = props;

  const [ranges, setRanges] = useState<any>([
    {
      startDate: dateFrom ? new Date(dateFrom) : null,
      endDate: dateTo ? new Date(dateTo) : null,
      key: "selection",
    },
  ]);

  const [showPicker, setShowPicker] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [lastSelectedDate, setLastSelectedDate] = useState<string | null>(null);
  const [hoveredPreviewDate, setHoveredPreviewDate] = useState<string | null>(
    null,
  );
  const popperContainer = useRef(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 4],
        },
      },
      {
        // When there's no room above or below (e.g. the input sits low in a
        // short sidebar panel), fall back to opening sideways instead of
        // getting clipped by the viewport.
        name: "flip",
        options: {
          fallbackPlacements: ["top-start", "left-start", "right-start"],
        },
      },
      {
        name: "preventOverflow",
        options: {
          padding: 8,
        },
      },
    ],
  });

  const handleChange = (item: any) => {
    const newRanges = [item.selection];

    setRanges(newRanges);

    const newStartDate = moment(newRanges[0]?.startDate).format(
      momentDateFormat,
    );
    const newEndDate = moment(newRanges[0]?.endDate).format(momentDateFormat);

    const isRangeSelected = newStartDate !== newEndDate;

    const isSameDateClickedTwice =
      newStartDate === newEndDate && lastSelectedDate === newStartDate;

    const isComplete = isRangeSelected || isSameDateClickedTwice;

    if (onRangeComplete) {
      // Only commit once the full range is picked, so the first click
      // (start date only) doesn't trigger a data fetch/save on its own.
      if (isComplete) {
        onRangeComplete(newStartDate, newEndDate);
        setShowPicker(false);
        setLastSelectedDate(null);
        setHoveredPreviewDate(null);
      } else {
        setLastSelectedDate(newStartDate);
      }
      return;
    }

    setDateFrom(newStartDate);
    setDateTo(newEndDate);

    if (isComplete) {
      setShowPicker(false);
      setLastSelectedDate(null);
      setHoveredPreviewDate(null);
    } else {
      setLastSelectedDate(newStartDate);
    }
  };

  const handleDayHover = useCallback(
    (day: Date) => {
      if (!lastSelectedDate) return;

      setHoveredPreviewDate(moment(day).format(momentDateFormat));
    },
    [lastSelectedDate],
  );

  const renderDayContent = useCallback(
    (day: Date) => (
      <span onMouseEnter={() => handleDayHover(day)}>
        {moment(day).format("D")}
      </span>
    ),
    [handleDayHover],
  );

  const commitPendingSelection = useCallback(() => {
    if (!lastSelectedDate) return;

    if (onRangeComplete) {
      onRangeComplete(lastSelectedDate, lastSelectedDate);
    } else {
      setDateFrom(lastSelectedDate);
      setDateTo(lastSelectedDate);
    }

    setLastSelectedDate(null);
    setHoveredPreviewDate(null);
  }, [lastSelectedDate, onRangeComplete, setDateFrom, setDateTo]);

  const closePicker = useCallback(() => {
    commitPendingSelection();
    setShowPicker(false);
  }, [commitPendingSelection]);

  const handleClear = (e: any) => {
    e.stopPropagation();

    setDateFrom(null);
    setDateTo(null);
    setShowPicker(false);
    setLastSelectedDate(null);
    setHoveredPreviewDate(null);
  };

  const selection = ranges?.[0];
  const effectiveMinDate =
    lastSelectedDate && selection?.startDate
      ? minDate && minDate > selection.startDate
        ? minDate
        : selection.startDate
      : minDate;
  const shownDate =
    minDate && selection?.endDate && selection.endDate < minDate
      ? minDate
      : selection?.startDate || new Date();

  useEffect(() => {
    setRanges([
      {
        startDate: dateFrom ? new Date(dateFrom) : new Date(),
        endDate: dateTo
          ? new Date(dateTo)
          : addDays(new Date(), startingRangeDays || 0),
        key: "selection",
      },
    ]);
  }, [dateFrom, dateTo, startingRangeDays]);

  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (showPicker) {
      onOpenRef.current?.();
    }
  }, [showPicker]);

  useEffect(() => {
    setIsPickerOpen && setIsPickerOpen(showPicker);

    if (!showPicker) {
      setLastSelectedDate(null);
      setHoveredPreviewDate(null);
    }
  }, [showPicker, setIsPickerOpen]);

  useOnClickOutside(popperContainer, closePicker);

  return (
    <div
      ref={setReferenceElement}
      onClick={() => setShowPicker(true)}
      className="custom-date-range"
      data-qa={dataQa}
    >
      <div
        className={classNames("custom-date-range__input", {
          "custom-date-range__input--focused": showPicker,
          "custom-date-range__input--error": showError,
        })}
      >
        {!showError ? (
          <>
            <CalendarIcon />
            {lastSelectedDate || dateFrom || (
              <span className="custom-date-range__input-placeholder">
                <IDHFormattedMessage
                  id="ws_start_date"
                  defaultMessage="Start date"
                />
              </span>
            )}
            <span className="custom-date-range__input-separator" />
            {lastSelectedDate ? (
              <span className="custom-date-range__input-preview-date">
                {hoveredPreviewDate || lastSelectedDate}
              </span>
            ) : (
              dateTo || (
                <span className="custom-date-range__input-placeholder">
                  <IDHFormattedMessage
                    id="ws_end_date"
                    defaultMessage="End date"
                  />
                </span>
              )
            )}
            {dateFrom && dateTo && !hideClearButton ? (
              <CrossIcon
                className="custom-date-range__clear-btn"
                onClick={(e) => handleClear(e)}
              />
            ) : null}
          </>
        ) : (
          <span className="custom-date-range__input-error">{errorMessage}</span>
        )}
      </div>
      <Portal container={overlaysRef}>
        {showPicker && (
          <div ref={popperContainer}>
            <div
              ref={setPopperElement}
              style={{ ...styles.popper, zIndex: zIndex.dropdown }}
              {...attributes.popper}
            >
              <DateRangePicker
                className={classNames({
                  "rdrDateRangePickerWrapper--to-right": toRight,
                })}
                ranges={ranges}
                onChange={handleChange}
                staticRanges={[]}
                inputRanges={[]}
                fixedHeight
                months={2}
                minDate={effectiveMinDate}
                maxDate={maxDate}
                direction="horizontal"
                showDateDisplay={false}
                color="5D78FF"
                weekStartsOn={1}
                weekdayDisplayFormat="EEEEEE"
                shownDate={shownDate}
                dayContentRenderer={renderDayContent}
              />
            </div>
          </div>
        )}
      </Portal>
    </div>
  );
};
